<template>
  <div>
    <RequestDocumentsTable
      ref="table"
      :documents="requestedDocuments"
      @click:deleteMultiple="handleRemoveDocuments"
      @click:completeReceiptMultiple="handleClickCompleteReceipt"
      @click:newMultiple="handleClickNew"
      @click:update="handleClickUpdate"
      @click:undoReceiptMultiple="handleUndoReceiptMultiple"
      @click:undoReceipt="handleUndoReceipt"
    />

    <ConfirmationDialog ref="confirmDialog" />

    <RequestedDocumentReceiptDialog
      ref="receiptDialog"
      @submit="handleCompleteReceipt"
    />

    <ProductRequestedDocumentFormDialog
      ref="FormDialog"
      :product-id="productId"
      :lead-type-id="leadTypeId"
      :subtype-id="subtypeId"
      @submit="handleSaveDocument"
    />
  </div>
</template>

<script>
import RequestDocumentsTable from "../RequestDocumentsTable.vue";
import ConfirmationDialog from "@/components/tools/ConfirmationDialog";
import i18n from "@/mixins/i18n";
import {
  listProductRequestedDocuments,
  createProductRequestedDocument,
  updateProductRequestedDocument,
} from "@/services/products_requested_documents";
import {
  deleteRequestedDocuments,
  updateReceiptDate,
  updateReceiptDatesBulk,
} from "@/services/requested_documents";
import ToastsMixin from "@/mixins/ToastsMixin";
import RequestedDocumentReceiptDialog from "../RequestedDocumentReceiptDialog.vue";
import ProductRequestedDocumentFormDialog from "./ProductRequestedDocumentFormDialog.vue";

export default {
  mixins: [i18n, ToastsMixin],

  components: {
    RequestDocumentsTable,
    ConfirmationDialog,
    RequestedDocumentReceiptDialog,
    ProductRequestedDocumentFormDialog,
  },

  props: {
    productId: Number,
    leadTypeId: {
      type: [String, Number],
      default: null,
    },
    subtypeId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      i18n_prefix: "products",
      requestedDocuments: [],
    };
  },

  methods: {
    async loadDocuments(productId) {
      try {
        const response = await listProductRequestedDocuments(productId);
        this.requestedDocuments = response.data;
        this.$emit("change", this.requestedDocuments);
      } catch (error) {
        console.error(error);
      }
    },
    reset() {
      this.requestedDocuments = [];
    },

    async handleRemoveDocuments(documents) {
      if (!documents.length) return;

      const count = documents.length;
      const title = `${this.__("Excluir", "products")} ${count} ${
        count === 1
          ? this.__("anexo", "products")
          : this.__("anexos", "products")
      }?`;
      const description = `${this.__(
        "A ação irá remover",
        "products"
      )} ${count} ${
        count === 1
          ? this.__("anexo", "products")
          : this.__("anexos", "products")
      } ${this.__(
        "do produto, não será possível desfazer a ação",
        "products"
      )}`;

      this.$refs.confirmDialog.confirm(
        title,
        async ({ close, startLoading, stopLoading }) => {
          startLoading();
          try {
            const ids = documents.map((d) => d.id).filter(Boolean);

            if (ids.length) {
              await deleteRequestedDocuments(ids);
            }

            const selectedIds = documents.map((document) => document.id);
            this.requestedDocuments = this.requestedDocuments.filter(
              (requestedDocument) => !selectedIds.includes(requestedDocument.id)
            );

            this.toastSuccess(
              this.__("Documentos excluídos com sucesso", "products")
            );
            close();
            this.$refs.table.unselectAll();
          } catch (error) {
            console.error(error);

            let message = this.__("Erro ao excluir documentos", "products");
            if (error?.response?.data?.message) {
              message = error.response.data.message;
            } else if (error?.message) {
              message = error.message;
            }

            this.toastError(message);
          } finally {
            stopLoading();
          }
        },
        null,
        {
          description,
          confirmText: this.__("Excluir", "products"),
          cancelText: this.__("cancelar", "products"),
          color: "error",
        }
      );
    },

    handleClickCompleteReceipt(items) {
      this.$refs.receiptDialog.open(items);
    },

    async handleCompleteReceipt(payload) {
      this.$refs.receiptDialog.setLoading(true);

      const ids = payload.documents.map((document) => document.id);

      try {
        const response = await updateReceiptDatesBulk(
          ids,
          payload.form.receipt_date
        );
        const { updated = [], skipped = [] } = response.data ?? response;

        this.loadDocuments(this.productId);

        if (skipped.length < 1) {
          this.toastSuccess(this.__("Data registrada com sucesso", "products"));
          this.$refs.receiptDialog.close();
          this.$refs.table.unselectAll();
          return;
        }

        this.$refs.receiptDialog.setMessages([
          ...updated.map((item) => ({
            type: "success",
            name: item.category.name,
            reason: "Sucesso",
          })),
          ...skipped.map((item) => ({
            type: "error",
            name: item.name,
            reason: item.reason,
          })),
        ]);

        this.$refs.receiptDialog.documents =
          this.$refs.receiptDialog.documents.filter((item) =>
            skipped.map((i) => i.id).includes(item.id)
          );
      } catch (error) {
        console.error(error);
        this.toastError("Falha ao salvar recebimento");
      } finally {
        this.$refs.receiptDialog.setLoading(false);
      }
    },

    handleClickNew() {
      const excludedIds = this.requestedDocuments
        .map((doc) => doc.category?.id)
        .filter(Boolean);
      this.$refs.FormDialog.open(null, excludedIds);
    },

    handleClickUpdate(item) {
      this.$refs.FormDialog.open(item, []);
    },

    async handleSaveDocument(payload) {
      this.$refs.FormDialog.setLoading(true);
      this.$refs.FormDialog.clearMessages();

      try {
        const isEdit = !!payload.id;
        const formData = {
          category_id: payload.category_id,
          request_date: payload.request_date,
        };

        if (payload.receipt_date) {
          formData.receipt_date = payload.receipt_date;
        }

        if (isEdit) {
          await updateProductRequestedDocument(
            this.productId,
            payload.id,
            formData
          );
          this.toastSuccess(
            this.__("Documento alterado com sucesso", "products")
          );
        } else {
          await createProductRequestedDocument(this.productId, formData);
          this.toastSuccess(
            this.__("Documento adicionado com sucesso", "products")
          );
        }

        this.$refs.FormDialog.close();
        await this.loadDocuments(this.productId);
      } catch (error) {
        console.error(error);

        let errorMessage = this.__("Erro ao salvar documento", "products");
        let errorField = "general";

        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.errors) {
          const errors = error.response.data.errors;
          errorField = Object.keys(errors)[0] || "general";
          if (errors[errorField]?.length) {
            errorMessage = errors[errorField][0];
          }
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this.$refs.FormDialog.setErrors({
          [errorField]: [errorMessage],
        });

        this.$refs.FormDialog.setMessage(
          "error",
          this.__("Erro", "products"),
          errorMessage
        );
      } finally {
        this.$refs.FormDialog.setLoading(false);
      }
    },

    async handleUndoReceipt(item) {
      try {
        const documentId = item.id;
        if (!documentId) throw new Error("Documento inválido");

        await updateReceiptDate(documentId, { receipt_date: null });

        const index = this.requestedDocuments.findIndex(
          (document) => document.id === documentId
        );
        if (index !== -1) {
          this.$set(this.requestedDocuments, index, {
            ...this.requestedDocuments[index],
            receipt_date: null,
          });
        }

        this.$emit("change", this.requestedDocuments);
        this.toastSuccess(
          this.__("Recebimento desfeito com sucesso", "products")
        );
      } catch (error) {
        console.error(error);
        this.toastError(this.__("Erro ao desfazer recebimento", "products"));
      }
    },

    async handleUndoReceiptMultiple(documents) {
      const docsWithReceipt = documents.filter(
        (document) => document.receipt_date
      );
      if (!docsWithReceipt.length) return;

      const count = docsWithReceipt.length;
      const title = `${this.__(
        "Desfazer recebimento de",
        "products"
      )} ${count} ${
        count === 1
          ? this.__("anexo", "products")
          : this.__("anexos", "products")
      }?`;
      const description = `${this.__(
        "A ação irá desfazer o recebimento de",
        "products"
      )} ${count} ${
        count === 1
          ? this.__("anexo", "products")
          : this.__("anexos", "products")
      }`;

      this.$refs.confirmDialog.confirm(
        title,
        async ({ close, startLoading, stopLoading }) => {
          startLoading();
          try {
            const ids = docsWithReceipt
              .map((document) => document.id)
              .filter(Boolean);
            if (!ids.length) throw new Error("Nenhum documento válido");

            await updateReceiptDatesBulk(ids, null);

            this.requestedDocuments = this.requestedDocuments.map((document) =>
              ids.includes(document.id)
                ? { ...document, receipt_date: null }
                : document
            );

            this.$emit("change", this.requestedDocuments);
            this.toastSuccess(
              this.__("Recebimento(s) desfeito(s) com sucesso", "products")
            );
            close();
            this.$refs.table.unselectAll();
          } catch (error) {
            console.error(error);

            let message = this.__("Erro ao desfazer recebimento", "products");
            if (error?.response?.data?.message) {
              message = error.response.data.message;
            } else if (error?.message) {
              message = error.message;
            }

            this.toastError(message);
          } finally {
            stopLoading();
          }
        },
        null,
        {
          description,
          confirmText: this.__("Desfazer", "products"),
          cancelText: this.__("Cancelar", "products"),
          color: "primary",
        }
      );
    },
  },

  computed: {},

  watch: {
    productId: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.reset();
          return;
        }

        this.loadDocuments(value);
      },
    },
  },
};
</script>
